import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Search, CheckCircle2, AlertTriangle, Loader2,
  ChevronDown, ChevronRight, ShieldCheck, Database, Clock, PlayCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/skillbridge`;

const RUNNING_STATES = ['running', 'dry_running', 'applying', 'finalizing'];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const STATUS_META = {
  idle: { label: 'Idle', cls: 'bg-slate-100 text-slate-700' },
  running: { label: 'Acquiring…', cls: 'bg-blue-100 text-blue-800' },
  cooloff_wave: { label: 'Cooling off (rate limit)', cls: 'bg-amber-100 text-amber-800' },
  acquired: { label: 'Acquisition complete', cls: 'bg-indigo-100 text-indigo-800' },
  finalizing: { label: 'Finalizing diff…', cls: 'bg-blue-100 text-blue-800' },
  dry_running: { label: 'Running dry-run…', cls: 'bg-blue-100 text-blue-800' },
  dry_run_ready: { label: 'Dry-run ready to review', cls: 'bg-emerald-100 text-emerald-800' },
  blocked: { label: 'Blocked (completeness guard)', cls: 'bg-red-100 text-red-800' },
  applying: { label: 'Applying updates…', cls: 'bg-blue-100 text-blue-800' },
  applied: { label: 'Updates applied', cls: 'bg-emerald-100 text-emerald-800' },
  apply_aborted: { label: 'Apply aborted', cls: 'bg-red-100 text-red-800' },
  apply_error: { label: 'Apply error', cls: 'bg-red-100 text-red-800' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-800' },
  dry_run_error: { label: 'Dry-run error', cls: 'bg-red-100 text-red-800' },
};

function StatCell({ label, value, testId, tone = 'default' }) {
  const toneCls = {
    default: 'text-slate-900', warn: 'text-amber-700', danger: 'text-red-700', ok: 'text-emerald-700',
  }[tone];
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

function DiffPill({ label, value, cls, testId }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${cls}`} data-testid={testId}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide">{label}</div>
    </div>
  );
}

export default function SkillBridgeSyncAdmin() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [showResolution, setShowResolution] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/status`, { withCredentials: true });
      setStatus(r.data);
      return r.data;
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const prog = status?.progress || {};
  const st = prog.status || 'idle';
  const meta = STATUS_META[st] || STATUS_META.idle;
  const isRunning = RUNNING_STATES.includes(st);
  const applyStale = !!status?.apply_stale;  // stale/interrupted apply → offer Resume

  const discovered = prog.providers_discovered || 0;
  const chunkSizes = prog.chunk_sizes || [];
  const currentChunk = prog.current_chunk || 0;
  const providerIndex = prog.provider_index || 0;
  const processed = st === 'acquired' || st === 'dry_run_ready' || st === 'applied' || st === 'dry_running' || st === 'applying'
    ? discovered
    : chunkSizes.slice(0, currentChunk).reduce((a, b) => a + b, 0) + providerIndex;
  const pct = discovered ? Math.min(100, Math.round((processed / discovered) * 100)) : 0;

  const dry = (!isRunning && prog.dry_run) ? prog.dry_run : null;
  const failed = prog.failed || [];
  const unresolved = prog.unresolved || [];
  const incomplete = prog.incomplete || [];

  const runAction = async (path, okMsg) => {
    setActing(true);
    try {
      await axios.post(`${API}/${path}`, {}, { withCredentials: true });
      toast.success(okMsg);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="sb-sync-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="skillbridge-sync-admin">
      <button
        onClick={() => navigate('/dashboard')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
        data-testid="sb-sync-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <ShieldCheck className="h-6 w-6 text-brand" /> SkillBridge Sync
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Pull the latest opportunities directly from the official DoD SkillBridge source, review a
            dry-run diff, then apply. Nothing is written to the live board until you press{' '}
            <span className="font-medium">Apply Updates</span>.
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={fetchStatus}
          data-testid="sb-sync-refresh" className="shrink-0"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Top status row */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card className="p-4" data-testid="sb-sync-status-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
            <Database className="h-3.5 w-3.5" /> Status
          </div>
          <Badge className={`mt-2 ${applyStale ? 'bg-amber-100 text-amber-800' : meta.cls}`} data-testid="sb-sync-status-badge">
            {applyStale ? 'Apply interrupted' : meta.label}
          </Badge>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
            <CheckCircle2 className="h-3.5 w-3.5" /> Live SkillBridge jobs
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900" data-testid="sb-sync-active-count">
            {(status?.current_active_skillbridge ?? 0).toLocaleString()}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
            <Clock className="h-3.5 w-3.5" /> Last successful sync
          </div>
          <div className="mt-2 text-sm font-medium text-slate-800" data-testid="sb-sync-last-sync">
            {fmtDate(status?.last_successful_sync?.completed_at)}
          </div>
        </Card>
      </div>

      {/* Acquisition progress */}
      <Card className="mb-5 p-5" data-testid="sb-sync-acquisition-card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">Acquisition</h2>
          {prog.stage && (
            <span className="text-xs text-slate-500" data-testid="sb-sync-stage">
              Stage: {prog.stage}{prog.total_chunks ? ` · Chunk ${Math.min(currentChunk + 1, prog.total_chunks)} of ${prog.total_chunks}` : ''}
            </span>
          )}
        </div>

        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-slate-600">
            Providers processed
          </span>
          <span className="font-medium text-slate-900" data-testid="sb-sync-progress-text">
            {processed.toLocaleString()} / {discovered.toLocaleString()} ({pct}%)
          </span>
        </div>
        <Progress value={pct} className="h-2.5" data-testid="sb-sync-progress-bar" />

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <StatCell label="Unique records" value={(prog.records_collected ?? 0).toLocaleString()} testId="sb-stat-records" />
          <StatCell label="Successful" value={(prog.successful ?? 0).toLocaleString()} testId="sb-stat-successful" tone="ok" />
          <StatCell label="Empty" value={(prog.empty_count ?? 0).toLocaleString()} testId="sb-stat-empty" />
          <StatCell label="Recovered" value={(prog.recovered ?? 0).toLocaleString()} testId="sb-stat-recovered" tone="ok" />
          <StatCell label="Failures" value={failed.length.toLocaleString()} testId="sb-stat-failed" tone={failed.length ? 'warn' : 'default'} />
          <StatCell label="Unresolved" value={unresolved.length.toLocaleString()} testId="sb-stat-unresolved" tone={unresolved.length ? 'danger' : 'default'} />
          <StatCell label="Incomplete" value={incomplete.length.toLocaleString()} testId="sb-stat-incomplete" tone={incomplete.length ? 'warn' : 'default'} />
          <StatCell label="Discovered" value={discovered.toLocaleString()} testId="sb-stat-discovered" />
        </div>

        {(failed.length > 0 || unresolved.length > 0 || incomplete.length > 0) && (
          <div className="mt-4">
            <button
              onClick={() => setShowFailed((v) => !v)}
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
              data-testid="sb-sync-toggle-failed"
            >
              {showFailed ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Failed / unresolved provider details
            </button>
            {showFailed && (
              <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3 text-xs" data-testid="sb-sync-failed-details">
                {[['Unresolved', unresolved, 'text-red-700'], ['Failed (retried)', failed, 'text-amber-700'], ['Incomplete', incomplete, 'text-amber-700']].map(
                  ([label, list, cls]) => list.length > 0 && (
                    <div key={label}>
                      <div className={`mb-1 font-semibold ${cls}`}>{label} ({list.length})</div>
                      <div className="max-h-40 overflow-y-auto font-mono text-slate-600">
                        {list.map((p, i) => <div key={i}>{p}</div>)}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {isRunning && (
          <p className="mt-4 flex items-center gap-2 text-xs text-slate-500" data-testid="sb-sync-running-note">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Live — this page refreshes automatically. The full official crawl can take up to ~90 minutes.
          </p>
        )}
      </Card>

      {/* Dry-run diff */}
      {dry && (
        <Card className="mb-5 p-5" data-testid="sb-sync-diff-card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">Dry-run diff (preview only)</h2>
            {prog.safe_to_apply
              ? <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Safe to apply</Badge>
              : <Badge className="bg-red-100 text-red-800"><AlertTriangle className="mr-1 h-3.5 w-3.5" />Not safe to apply</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <DiffPill label="New" value={(dry.new ?? 0).toLocaleString()} cls="border-emerald-200 bg-emerald-50 text-emerald-800" testId="sb-diff-new" />
            <DiffPill label="Changed" value={(dry.changed ?? 0).toLocaleString()} cls="border-blue-200 bg-blue-50 text-blue-800" testId="sb-diff-changed" />
            <DiffPill label="Missing" value={(dry.missing_from_source ?? 0).toLocaleString()} cls="border-red-200 bg-red-50 text-red-800" testId="sb-diff-missing" />
            <DiffPill label="Reactivated" value={(dry.reactivated ?? 0).toLocaleString()} cls="border-indigo-200 bg-indigo-50 text-indigo-800" testId="sb-diff-reactivated" />
            <DiffPill label="Unchanged" value={(dry.unchanged ?? 0).toLocaleString()} cls="border-slate-200 bg-slate-50 text-slate-700" testId="sb-diff-unchanged" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500 sm:grid-cols-4">
            <div>Source records: <span className="font-medium text-slate-700">{(dry.source_records_total ?? 0).toLocaleString()}</span></div>
            <div>Source unique: <span className="font-medium text-slate-700">{(dry.source_unique ?? 0).toLocaleString()}</span></div>
            <div>Current active: <span className="font-medium text-slate-700">{(dry.current_active ?? 0).toLocaleString()}</span></div>
            <div>Profiles to refresh: <span className="font-medium text-slate-700">{(dry.profiles_would_refresh ?? 0).toLocaleString()}</span></div>
          </div>
          {(dry.protected_missing ?? 0) > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-2" data-testid="sb-diff-removal-breakdown">
              <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-red-800">
                Verified removals → inactivate: <span className="font-semibold" data-testid="sb-diff-eligible-removals">{(dry.eligible_removals ?? 0).toLocaleString()}</span>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800">
                Protected (incomplete provider, kept active): <span className="font-semibold" data-testid="sb-diff-protected-missing">{(dry.protected_missing ?? 0).toLocaleString()}</span>
              </div>
            </div>
          )}
          {prog.protection_note && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="sb-diff-protection-note">
              {prog.protection_note}
            </div>
          )}
          {prog.safety_reasons?.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="sb-diff-safety-reasons">
              <div className="mb-1 font-semibold">Why apply is held</div>
              <ul className="list-disc pl-4">
                {prog.safety_reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {prog.verification && (
            <div className="mt-4">
              <button
                onClick={() => setShowResolution((v) => !v)}
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
                data-testid="sb-toggle-resolution"
              >
                {showResolution ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                Resolution details
              </button>
              {showResolution && (
                <div className="mt-3 space-y-4 rounded-lg bg-slate-50 p-3 text-xs" data-testid="sb-resolution-details">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div>Missing re-added (still active): <span className="font-semibold text-emerald-700">{prog.verification.missing_still_active_readded ?? 0}</span></div>
                    <div>Unresolved opportunities: <span className="font-semibold text-slate-800">{prog.verification.missing_unresolved?.length ?? 0}</span></div>
                    <div>Providers confirmed zero: <span className="font-semibold text-slate-800">{prog.verification.providers_confirmed_zero ?? 0}</span></div>
                    <div>Providers unqueryable: <span className="font-semibold text-slate-800">{prog.verification.providers_could_not_query?.length ?? 0}</span></div>
                  </div>
                  <div>
                    <div className="mb-1 font-semibold text-red-700">Confirmed removed → would inactivate ({prog.verification.missing_confirmed_removed?.length ?? 0})</div>
                    <div className="max-h-48 overflow-y-auto">
                      {(prog.verification.missing_confirmed_removed || []).map((c, i) => (
                        <div key={i} className="border-b border-slate-200 py-1">
                          <span className="font-mono text-slate-500">{c.external_id}</span> · {c.job_id} · {c.provider} · {c.location} · <span className="text-slate-500">{c.provider_current_official_positions} current official positions</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {prog.verification.missing_unresolved?.length > 0 && (
                    <div>
                      <div className="mb-1 font-semibold text-amber-700">Unresolved opportunities ({prog.verification.missing_unresolved.length})</div>
                      {prog.verification.missing_unresolved.map((c, i) => (
                        <div key={i}><span className="font-mono">{c.external_id}</span> · {c.provider}</div>
                      ))}
                    </div>
                  )}
                  {prog.verification.providers_could_not_query?.length > 0 && (
                    <div>
                      <div className="mb-1 font-semibold text-amber-700">Unresolved providers ({prog.verification.providers_could_not_query.length})</div>
                      {prog.verification.providers_could_not_query.map((p, i) => (
                        <div key={i}>{p.provider}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3" data-testid="sb-sync-actions">
        {applyStale && (
          <Button
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => runAction('apply', 'Resuming interrupted apply…')}
            disabled={acting}
            data-testid="sb-sync-resume-apply-btn"
          >
            <PlayCircle className="mr-1.5 h-4 w-4" /> Resume Apply
          </Button>
        )}
        <Button
          onClick={() => runAction('check', 'Fresh acquisition started')}
          disabled={acting || isRunning}
          data-testid="sb-sync-check-btn"
        >
          <Search className="mr-1.5 h-4 w-4" /> Check for Updates
        </Button>

        {(st === 'acquired' || st === 'blocked' || st === 'error' || st === 'dry_run_error') && (
          <Button
            variant="secondary"
            onClick={() => runAction('dry-run', 'Dry-run started')}
            disabled={acting}
            data-testid="sb-sync-dryrun-btn"
          >
            <PlayCircle className="mr-1.5 h-4 w-4" /> Review Diff (Dry-run)
          </Button>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={acting || st !== 'dry_run_ready' || !prog.safe_to_apply}
              data-testid="sb-sync-apply-btn"
            >
              <CheckCircle2 className="mr-1.5 h-4 w-4" /> Apply Updates
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="sb-sync-apply-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Apply SkillBridge updates?</AlertDialogTitle>
              <AlertDialogDescription>
                This writes the reviewed snapshot to the live board:
                {dry && (
                  <span className="mt-2 block font-medium text-slate-700">
                    +{dry.new} new · {dry.changed} changed · {dry.missing_from_source} missing → inactive ·
                    {' '}{dry.reactivated} reactivated
                  </span>
                )}
                Existing Job IDs and favorites are preserved. This cannot be undone from this screen.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="sb-sync-apply-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => runAction('apply', 'Applying updates…')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="sb-sync-apply-confirm"
              >
                Apply now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {(prog.errors?.length > 0 || prog.dry_run_errors?.length > 0) && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="sb-sync-errors">
          <div className="mb-1 font-semibold">Errors</div>
          {[...(prog.errors || []), ...(prog.dry_run_errors || [])].map((e, i) => <div key={i} className="font-mono">{e}</div>)}
        </div>
      )}
    </div>
  );
}
